import { useMutation, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import type {
  RecordExternalHandoffConsentPayload,
  RecordExternalHandoffConsentResponse,
} from '@usacsmideo/types';
import { functions } from '../constants/firebaseModular';
import { useAuth } from '../contexts/AuthContext';

/**
 * Records a viewer's "Open in maps" external-handoff consent ack for the Session
 * Location Map (CU-86ajd3yt0, epic CU-86ajd302x).
 *
 * The handoff is the single third-party PHI-boundary crossing in the feature —
 * the coordinate leaves MIDEO to Apple/Google/OpenStreetMap tied to the viewer.
 * This callable writes an append-only, server-side audit ack to
 * `users/{uid}/consentAcks/` (the client NEVER writes Firestore directly) and,
 * when `dontAskAgain` is set, records the PER-VIEWER suppression state honored
 * across all sessions and devices.
 *
 * On success we invalidate the sessions query so the folded `handoffConsent`
 * read path (`GetSessionsForViewerResponse.handoffConsent.dontAskAgain`)
 * refreshes and the dialog is suppressed on subsequent taps without a manual
 * refetch. Errors surface to the caller; the caller decides whether to still
 * perform the handoff (see SessionCard — we proceed on the user-initiated action
 * even if the ack write fails, since the consent was given in-UI).
 */
export function useRecordHandoffConsent() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;

  return useMutation<
    RecordExternalHandoffConsentResponse,
    unknown,
    RecordExternalHandoffConsentPayload
  >({
    mutationFn: async (payload) => {
      const call = httpsCallable<
        RecordExternalHandoffConsentPayload,
        RecordExternalHandoffConsentResponse
      >(functions, 'recordExternalHandoffConsent');
      const { data } = await call(payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      // Only the suppression-setting acks change the read path; still cheap to
      // invalidate unconditionally, but gate to avoid a needless refetch.
      if (variables.dontAskAgain) {
        queryClient.invalidateQueries({ queryKey: ['sessionsForViewer', uid] });
      }
    },
  });
}

export default useRecordHandoffConsent;
